<template>
  <!-- Readonly: fractional fill via a clipped overlay (avg-rating display) -->
  <div
    v-if="readonly"
    class="play-rating play-rating--readonly"
    :class="[`play-rating--${size}`, colorClass]"
    role="img"
    :aria-label="ariaLabel"
  >
    <div class="play-rating__row play-rating__row--empty">
      <svg
        v-for="i in max"
        :key="`e${i}`"
        class="play-rating__star"
        viewBox="0 0 24 24"
        aria-hidden="true"
      ><path :d="starPath" /></svg>
    </div>
    <div
      class="play-rating__row play-rating__row--fill"
      :style="{ width: fillPct }"
    >
      <svg
        v-for="i in max"
        :key="`f${i}`"
        class="play-rating__star"
        viewBox="0 0 24 24"
        aria-hidden="true"
      ><path :d="starPath" /></svg>
    </div>
  </div>

  <!-- Interactive: integer selection with hover preview -->
  <div
    v-else
    class="play-rating"
    :class="[`play-rating--${size}`, colorClass]"
    role="radiogroup"
    tabindex="-1"
    :aria-label="ariaLabel"
    @mouseleave="hoverValue = 0"
  >
    <button
      v-for="i in max"
      :key="i"
      type="button"
      class="play-rating__btn"
      :class="{ 'is-on': displayValue >= i }"
      role="radio"
      :aria-checked="value === i"
      :aria-label="`${i}`"
      @click="select(i)"
      @mouseenter="hoverValue = i"
      @focus="hoverValue = i"
      @blur="hoverValue = 0"
    >
      <svg
        class="play-rating__star"
        viewBox="0 0 24 24"
        aria-hidden="true"
      ><path :d="starPath" /></svg>
    </button>
  </div>
</template>

<script>
/**
 * PlayRating — star rating for the Play system (display + input).
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-form-rating>. Two modes from one component:
 *   readonly — fractional fill via a clipped overlay (e.g. avg "4.5" display)
 *   input    — integer selection with hover/focus preview, driven by v-model
 *
 * Uses an inline SVG star (crisp at any size, not an emoji). `error` switches
 * to the danger color for invalid form state.
 *
 * VUE3-MIGRATION: `model: { prop: 'value', event: 'input' }` — Vue 3 uses
 * `modelValue` / `update:modelValue`. Change this block + the emit name on
 * migration. No $listeners here.
 */
export default {
  name: 'PlayRating',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 5,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    /** Star color role when filled */
    variant: {
      type: String,
      default: 'accent',
      validator: v => ['accent', 'primary', 'highlight'].includes(v),
    },
    /** Invalid form state — overrides color with danger */
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoverValue: 0,
      starPath: 'M12 2.1l2.9 6.26 6.86.78-5.08 4.64 1.36 6.76L12 17.6l-6 3.34 1.36-6.76L2.28 9.54l6.86-.78z',
    }
  },
  computed: {
    displayValue() {
      return this.hoverValue || this.value
    },
    fillPct() {
      return `${(Math.min(Math.max(this.value, 0), this.max) / this.max) * 100}%`
    },
    colorClass() {
      return this.error ? 'play-rating--danger' : `play-rating--${this.variant}`
    },
    ariaLabel() {
      return `${this.value} ${this.$t ? this.$t('basic.of') : 'of'} ${this.max}`
    },
  },
  methods: {
    select(i) {
      this.$emit('input', i)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-rating {
  display: inline-flex;
  align-items: center;
  gap: 3px;

  /* filled color, overridable per variant below */
  --rating-on: var(--c-accent);

  &--accent    { --rating-on: var(--c-accent); }
  &--primary   { --rating-on: var(--c-primary); }
  &--highlight { --rating-on: var(--c-highlight-strong); }
  &--danger    { --rating-on: var(--c-danger); }

  /* ── Star sizes ── */
  &--sm { --star: 16px; }
  &--md { --star: 22px; }
  &--lg { --star: 28px; }

  &__star {
    width: var(--star, 22px);
    height: var(--star, 22px);
    display: block;
    fill: currentColor;
  }

  /* ── Readonly (layered fill) ── */
  &--readonly {
    position: relative;
    color: var(--line);
  }

  &__row {
    display: flex;
    gap: 3px;

    &--fill {
      position: absolute;
      inset: 0;
      overflow: hidden;
      white-space: nowrap;
      color: var(--rating-on);
    }
  }

  /* ── Interactive ── */
  &__btn {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: var(--line);
    display: inline-flex;
    transition: transform var(--dur-fast) var(--ease-bounce), color var(--dur-fast) var(--ease-out);
    -webkit-tap-highlight-color: transparent;

    &.is-on { color: var(--rating-on); }
    &:hover { transform: scale(1.15) rotate(-4deg); }
    &:active { transform: scale(.92); }

    &:focus-visible {
      outline: 2px solid var(--c-secondary);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-rating__btn { transition: color var(--dur-fast) var(--ease-out); }
  .play-rating__btn:hover,
  .play-rating__btn:active { transform: none; }
}
</style>
